import { useNavigate } from 'react-router-dom';
import { useHub, type HubNotification } from '../../state/hub';
import { Button } from '../../ui/Button';
import { cx } from '../../ui/cx';
import { toast } from '../../ui/Toast';

const ICON: Record<HubNotification['kind'], string> = { invite: '🎟️', dm: '💬', friend: '🤝' };

/**
 * Actionable live notifications from the hub (invites with Join/Decline, DMs
 * that open the chat, friend events). Rendered once by AppShell, top-right.
 */
export function HubNotifications() {
  const notifications = useHub((s) => s.notifications);
  const dismiss = useHub((s) => s.dismissNotification);
  const respondInvite = useHub((s) => s.respondInvite);
  const openChat = useHub((s) => s.openChat);
  const chatOpen = useHub((s) => s.chatOpen);
  const navigate = useNavigate();

  if (notifications.length === 0) return null;

  const join = async (n: HubNotification) => {
    dismiss(n.id);
    if (!n.inviteId || !n.showId) return;
    try {
      await respondInvite(n.inviteId, true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not accept the invite');
      return;
    }
    navigate(`/show/${n.showId}`);
  };

  const decline = async (n: HubNotification) => {
    dismiss(n.id);
    if (!n.inviteId) return;
    try {
      await respondInvite(n.inviteId, false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not decline the invite');
    }
  };

  return (
    <div className={cx('hub-notifs', chatOpen && 'beside-chat')} role="region" aria-label="Live notifications">
      {notifications.map((n) => {
        const clickable = n.kind === 'dm' || (n.kind === 'friend' && n.showId) || (n.kind === 'friend' && !n.showId);
        const onClick = () => {
          if (n.kind === 'dm' && n.userId) {
            dismiss(n.id);
            openChat(n.userId);
          } else if (n.kind === 'friend' && n.showId) {
            dismiss(n.id);
            navigate(`/show/${n.showId}`);
          } else if (n.kind === 'friend') {
            dismiss(n.id);
            navigate('/friends');
          }
        };
        return (
          <div
            key={n.id}
            className={cx('hub-notif', `hub-notif-${n.kind}`, clickable && 'hub-notif-clickable')}
            role={n.kind === 'invite' ? 'alertdialog' : 'status'}
            aria-label={n.title}
            onClick={clickable ? onClick : undefined}
            onKeyDown={
              clickable
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onClick();
                    }
                  }
                : undefined
            }
            tabIndex={clickable ? 0 : undefined}
          >
            <span className="hub-notif-icon" aria-hidden="true">
              {ICON[n.kind]}
            </span>
            <div style={{ minWidth: 0 }}>
              <div className="hub-notif-title">{n.title}</div>
              <div className="hub-notif-body">{n.body}</div>
            </div>
            <button
              type="button"
              className="toast-close"
              aria-label="Dismiss"
              onClick={(e) => {
                e.stopPropagation();
                dismiss(n.id);
              }}
            >
              ✕
            </button>
            {n.kind === 'invite' ? (
              <div className="hub-notif-actions">
                <Button size="sm" variant="gold" onClick={() => void join(n)}>
                  Join show
                </Button>
                <Button size="sm" variant="ghost" onClick={() => void decline(n)}>
                  Not now
                </Button>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
